/**
 * The site's Terms & Conditions, Privacy Policy and Refund Policy, written from
 * this site's settings in legalConfig.ts. Shown at /terms, /privacy and
 * /refund-policy (public, no sign-in) and linked from the checkout.
 *
 * Changing the wording: edit here, then move TERMS_VERSION / TERMS_UPDATED in
 * legalConfig.ts forward so purchases record which version the fan agreed to.
 */

import { LEGAL } from './legalConfig'

export interface LegalSection { title: string; paras?: string[]; bullets?: string[] }
export interface LegalDoc { title: string; intro: string[]; sections: LegalSection[] }
export type LegalKind = 'terms' | 'privacy' | 'refunds'

const L = LEGAL
const site = L.domain
const email = L.supportEmail
const O = L.offer

function membershipSection(): LegalSection {
  if (O.renews) {
    return {
      title: 'Membership and billing',
      paras: [
        `Membership gives your account access to the members' area of ${site}: your conversation with ${L.creator}, exclusive content and the updates posted while your membership is active.`,
      ],
      bullets: [
        'Memberships are billed monthly or yearly, at the price shown on the checkout before you pay.',
        'Your membership renews automatically at the end of each period and your card on file is charged the same price again, until you cancel.',
        'You can cancel at any time from the Account page. Cancelling stops future renewals, and you keep access until the end of the period you have already paid for.',
        'If we change the price, we will tell you before it applies to your next renewal.',
      ],
    }
  }
  return {
    title: 'Membership',
    paras: [
      `The ${O.name} costs ${O.price}, charged once. It gives your account ${O.length} of access to the members' area of ${site}: your conversation with ${L.creator}, exclusive content and the updates posted during that time.`,
    ],
    bullets: [
      'It is a one-time payment. It does not renew automatically and your card is not charged again for it.',
      `Your access starts as soon as the payment goes through and ends ${O.endsAfter} later. The confirmation screen shows the exact end date.`,
      'When your access ends, you can choose to buy again. Nothing happens automatically.',
    ],
  }
}

function terms(): LegalDoc {
  return {
    title: 'Terms & Conditions',
    intro: [
      `These Terms & Conditions ("Terms") govern your use of ${site} (the "Site"), the private members' site of ${L.siteTitle}. By creating an account, signing in or making a purchase, you agree to these Terms. If you do not agree, please do not use the Site.`,
    ],
    sections: [
      {
        title: 'Eligibility and your account',
        bullets: [
          'You must be at least 18 years old to use the Site.',
          'The information you give us must be accurate, and you are responsible for everything done with your account.',
          'Each account is for one person. You may not share, sell or transfer your account or your access.',
        ],
      },
      membershipSection(),
      {
        title: 'Other purchases',
        paras: [
          'Inside the Site you may buy individual items such as unlockable photos or videos, gallery images, tips, priority messages and wallet credit. The price is shown before you pay. Each is a one-time charge, and unlocked items are available in your account right away.',
          'Wallet balance and credit have no cash value. They cannot be withdrawn, transferred or exchanged for money, and can only be used on the Site.',
        ],
      },
      {
        title: 'Payments',
        bullets: [
          'Payments are processed securely by Stripe. We do not store your full card number.',
          `Charges appear on your card statement as ${L.statementName}.`,
          'You confirm that you are authorised to use the card you pay with.',
        ],
      },
      {
        title: 'Refunds',
        paras: [
          `All sales are final. The exceptions, how to ask, and the time limit are set out in our Refund Policy at ${site}/refund-policy.`,
        ],
      },
      {
        title: 'Use of content',
        bullets: [
          `All content on the Site belongs to ${L.creator} and is licensed to you for your own personal, non-commercial viewing only.`,
          'You may not copy, download, screenshot, record, share, post, resell or distribute any content, or give anyone else access to it.',
          'Breaking this rule can lead to your account being closed without a refund, and to legal action.',
        ],
      },
      {
        title: 'Confidentiality and conduct',
        bullets: [
          'Private messages and content are confidential. Do not share them with anyone, by any means.',
          'Do not harass, threaten or abuse anyone, impersonate anyone, or break the law while using the Site.',
          'Do not use bots or automated tools, or try to access accounts or systems that are not yours.',
        ],
      },
      {
        title: 'Chargebacks and disputes',
        paras: [
          `If something is wrong with a charge, please contact us first at ${email}. We answer billing questions and correct genuine mistakes.`,
          'When you buy, we record the date and time you agreed to these Terms, the version you agreed to, your IP address and your browser. If a chargeback is filed for a purchase you made and received, we may share that record, your purchase history and your account activity with the payment processor and your card issuer, and we may suspend your account while the dispute is open.',
        ],
      },
      {
        title: 'Ending your access',
        paras: [
          'We may suspend or close an account that breaks these Terms. You may stop using the Site at any time.',
        ],
      },
      {
        title: 'Disclaimers and liability',
        paras: [
          'The Site is provided "as is" and "as available". We do not promise it will always be uninterrupted or error-free.',
          'To the fullest extent the law allows, we are not liable for indirect or consequential losses, and our total liability to you is limited to the amount you paid us in the 12 months before the claim.',
        ],
      },
      {
        title: 'Changes to these Terms',
        paras: [
          'We may update these Terms. The date at the top of this page shows the current version. Changes apply from that date and do not change a purchase you have already made.',
        ],
      },
      {
        title: 'Governing law',
        paras: [
          'These Terms are governed by the laws of the United States. Any dispute will be resolved individually through binding arbitration under the rules of the American Arbitration Association, and not as part of a class or group action.',
        ],
      },
      {
        title: 'Contact',
        paras: [`Questions about these Terms: ${email}`],
      },
    ],
  }
}

function privacy(): LegalDoc {
  return {
    title: 'Privacy Policy',
    intro: [
      `This Privacy Policy explains what information ${site} (the "Site") collects, how it is used and shared, and the choices you have. By using the Site, you agree to this policy.`,
    ],
    sections: [
      {
        title: 'What we collect',
        bullets: [
          'Account details you give us: your name, email address, phone number and the answers in your access application.',
          'What you send on the Site: messages, tips, purchases and support requests.',
          'Purchase records: what you bought, when and for how much. Card payments are handled by Stripe; we only see the card type and last four digits.',
          'Agreement records: when you buy, the date and time you agreed to our Terms, the version you agreed to, your IP address and your browser.',
          'Technical information: your IP address and approximate location, device and browser type, pages visited and sign-in times.',
        ],
      },
      {
        title: 'How we use it',
        bullets: [
          'To run your account, deliver content and messages, and process payments.',
          'To send account messages such as sign-in codes, purchase confirmations and important notices.',
          'To keep the Site secure and prevent fraud, including investigating and answering chargebacks.',
          'To meet legal obligations.',
        ],
      },
      {
        title: 'Who we share it with',
        paras: ['We do not sell or rent your personal information. We share it only with:'],
        bullets: [
          'Service providers that run the Site for us: Stripe (payments), Resend (email), Twilio (text messages) and our hosting and storage providers.',
          'Payment processors and card issuers, when needed to process a payment or respond to a dispute.',
          'Authorities, when the law requires it or to protect the Site, its members or the creator.',
        ],
      },
      {
        title: 'Security and retention',
        paras: [
          'All traffic to the Site is encrypted, sign-in uses one-time codes, and access to your data is limited to the people who need it. No system is perfectly secure, but we take reasonable steps to protect your information.',
          'We keep your information while your account is active, and purchase and agreement records for as long as needed for accounting, legal and dispute purposes.',
        ],
      },
      {
        title: 'Your choices and rights',
        paras: [
          `You can ask to see, correct or delete your information by writing to ${email}. We reply within 30 days. Some records, such as purchase records, may need to be kept for legal reasons. California residents have the rights given by the CCPA; we do not sell personal information.`,
        ],
      },
      {
        title: 'Cookies and local storage',
        paras: [
          'The Site uses cookies and your browser\'s local storage to keep you signed in and remember your settings. We do not use them for advertising or to track you across other sites.',
        ],
      },
      {
        title: 'Adults only',
        paras: ['The Site is for people aged 18 and over. We do not knowingly collect information from anyone younger.'],
      },
      {
        title: 'Changes and contact',
        paras: [
          'If we change this policy, we will update the date at the top of this page.',
          `Questions about your privacy: ${email}`,
        ],
      },
    ],
  }
}

function refunds(): LegalDoc {
  return {
    title: 'Refund Policy',
    intro: [
      `This policy explains when ${site} gives refunds. It is part of our Terms & Conditions.`,
    ],
    sections: [
      {
        title: 'All sales are final',
        paras: [
          O.renews
            ? 'Memberships and in-site purchases are delivered right away, so payments are final once access is granted or the item is unlocked. Cancelling stops future renewals; it does not refund a period that has already been paid for.'
            : `The ${O.name} and in-site purchases are delivered right away, so payments are final once access is granted or the item is unlocked. There are no refunds for unused time.`,
        ],
      },
      {
        title: 'When we do refund',
        bullets: [
          'You were charged twice for the same purchase.',
          'You were charged but did not receive the access or the item you paid for, and we cannot fix it.',
        ],
        paras: [
          'If you see a payment you did not make, contact us straight away. We will secure the account and look into it with you.',
        ],
      },
      {
        title: 'How to ask',
        bullets: [
          `Write to ${email} within ${L.refundDays} days of the charge.`,
          'Send it from the email address on your account, and include the date, the amount and the last four digits of the card.',
          'We aim to reply within 3 business days. Approved refunds go back to the card that was charged.',
        ],
      },
      {
        title: 'How charges appear',
        paras: [`Payments on this Site appear on your card statement as ${L.statementName}.`],
      },
      {
        title: 'Chargebacks',
        paras: [
          `Please contact us before disputing a charge with your bank. We can usually fix a problem faster. See "Chargebacks and disputes" in our Terms & Conditions.`,
        ],
      },
    ],
  }
}

export function legalDoc(kind: LegalKind): LegalDoc {
  return kind === 'terms' ? terms() : kind === 'privacy' ? privacy() : refunds()
}
